/*
 * djvu-app.js — the /djvu-to-pdf page: pick -> survey -> settings -> PDF.
 *
 * Files are grouped into books first: a bundled DjVu or a one-page file is a
 * book; an indirect DjVu (an index file naming its parts) is ONE book made of
 * every selected file. Each book gets a survey (pages, text layer, outline,
 * colour) before anything is converted, because the settings depend on it —
 * a book without a text layer is where OCR matters.
 *
 * Billing: one unit per book, taken when the PDF is handed over (download),
 * never for a survey, a failure or a cancel.
 * ES2019.
 */
(function () {
  'use strict';

  var CFG = window.__DJVU_CFG || {};
  var I18N = CFG.i18n || {};
  var SLUG = 'djvu-to-pdf';

  function t(k, vars) {
    var s = I18N[k] != null ? String(I18N[k]) : k;
    if (vars) Object.keys(vars).forEach(function (v) { s = s.split(':' + v).join(String(vars[v])); });
    return s;
  }
  function $(id) { return document.getElementById(id); }
  function show(el, on) { if (el) el.hidden = !on; }
  function ae(name, data) { try { if (typeof window.__ae === 'function') window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (e) { /* telemetry never breaks the tool */ } }
  function funnel(stage, data) { try { if (typeof window.__aeFunnel === 'function') window.__aeFunnel(stage, data || {}); } catch (e) { /* */ } }
  function stage(name) { try { if (typeof window.__stage === 'function') window.__stage(name, { tool: SLUG }); } catch (e) { /* */ } }
  function toast(msg, kind) { if (typeof window.__toast === 'function') window.__toast(msg, kind || 'error'); }

  function fmtBytes(n) {
    var mb = n / 1048576;
    if (mb >= 1024) return (mb / 1024).toFixed(mb >= 10240 ? 0 : 1).replace('.', t('decimal_sep')) + ' ' + t('unit_gb');
    if (mb >= 10) return Math.round(mb) + ' ' + t('unit_mb');
    if (mb >= 0.1) return mb.toFixed(1).replace('.', t('decimal_sep')) + ' ' + t('unit_mb');
    return Math.max(1, Math.round(n / 1024)) + ' ' + t('unit_kb');
  }
  function fmtTime(sec) {
    if (sec < 60) return t('eta_sec', { n: Math.max(1, Math.round(sec / 5) * 5) });
    var m = Math.round(sec / 60);
    if (m < 60) return t('eta_min', { n: m });
    return t('eta_hour', { h: Math.floor(m / 60), m: m % 60 });
  }
  function countOutline(nodes) {
    var n = 0;
    (nodes || []).forEach(function (x) { n += 1 + countOutline(x.children); });
    return n;
  }

  // ------------------------------------------------------------------
  // Grouping picked files into books.
  // ------------------------------------------------------------------
  async function sniff(file) {
    var b;
    try { b = new Uint8Array(await file.slice(0, 32).arrayBuffer()); } catch (e) { return { kind: 'unreadable' }; }
    var p = (b[0] === 0x41 && b[1] === 0x54 && b[2] === 0x26 && b[3] === 0x54) ? 4 : 0;
    var four = function (o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); };
    if (b.length < p + 12 || four(p) !== 'FORM') return { kind: 'other' };
    var form = four(p + 8);
    if (form === 'DJVU') return { kind: 'page' };
    if (form === 'DJVI') return { kind: 'part' };
    if (form === 'DJVM') {
      if (b.length >= p + 21 && four(p + 12) === 'DIRM') return { kind: (b[p + 20] & 0x80) ? 'bundled' : 'index' };
      return { kind: 'bundled' };
    }
    return { kind: 'other' };
  }

  async function groupBooks(files) {
    var kinds = await Promise.all(files.map(sniff));
    var books = [], parts = [], index = null, other = [];
    files.forEach(function (f, i) {
      var k = kinds[i].kind;
      if (k === 'bundled') books.push({ files: [f], main: 0 });
      else if (k === 'index') { if (!index) index = f; else books.push({ files: [f], main: 0 }); }
      else if (k === 'page' || k === 'part') parts.push(f);
      else other.push(f);
    });
    if (index) {
      // an indirect book: the index plus every selected part file
      books.unshift({ files: [index].concat(parts), main: 0, indirect: true });
    } else {
      parts.forEach(function (f) { books.push({ files: [f], main: 0 }); });
    }
    books.forEach(function (b) { b.name = b.files[b.main].name; b.size = b.files.reduce(function (s, f) { return s + f.size; }, 0); });
    return { books: books, other: other };
  }

  // ------------------------------------------------------------------
  // State and views.
  // ------------------------------------------------------------------
  var S = {
    books: [],
    running: false,
    abort: null,
    ticket: null,
    ocr: null,
    delivered: new Set()
  };
  var views = ['djvIntake', 'djvReading', 'djvError', 'djvSetup', 'djvWork', 'djvResult'];
  function view(id) { views.forEach(function (v) { show($(v), v === id); }); }

  function closeAll() {
    S.books.forEach(function (b) { if (b.job) { try { b.job.close(); } catch (e) { /* gone */ } } b.blob = null; });
    S.books = [];
    S.delivered = new Set();
  }

  function reset() {
    if (S.abort) { try { S.abort.abort(); } catch (e) { /* */ } }
    closeAll();
    if (S.ticket) { try { S.ticket.abort(); } catch (e) { /* */ } S.ticket = null; }
    S.running = false;
    try { if (window.ToolUploader && window.ToolUploader.clear) window.ToolUploader.clear('djvUpload'); } catch (e) { /* */ }
    view('djvIntake');
  }

  function fail(msg, detail) {
    $('djvErrorText').textContent = msg;
    $('djvErrorDetail').textContent = detail || '';
    show($('djvErrorDetail'), !!detail);
    view('djvError');
  }

  // ------------------------------------------------------------------
  // Survey.
  // ------------------------------------------------------------------
  async function onFiles(list) {
    if (S.running) return;
    closeAll();
    var files = Array.prototype.slice.call(list || []);
    if (!files.length) return;
    stage('uploading');
    view('djvReading');
    $('djvReadingText').textContent = t('reading');
    setBar('djvReadingBar', 0);
    var g = await groupBooks(files);
    if (!g.books.length) {
      fail(t('err_not_djvu'), g.other.map(function (f) { return f.name; }).join(', '));
      ae('tool_error', { error_type: 'not_djvu', n: files.length });
      return;
    }
    S.books = g.books;
    for (var i = 0; i < S.books.length; i++) {
      var b = S.books[i];
      b.job = window.DjVuJob.create({ workerUrl: CFG.workerUrl, files: b.files, main: b.main });
      try {
        var prefix = S.books.length > 1 ? (i + 1) + '/' + S.books.length + ' · ' : '';
        b.info = await b.job.inspect(function (p) {
          $('djvReadingText').textContent = prefix + t('reading_pages', { done: p.done, total: p.total });
          setBar('djvReadingBar', p.total ? p.done / p.total : 0);
        });
        summarise(b);
      } catch (err) {
        b.error = err;
        ae('tool_error', { error_type: 'inspect_' + ((err && err.code) || 'error'), bytes: b.size });
      }
    }
    var ok = S.books.filter(function (b) { return b.info && !b.error; });
    if (!ok.length) {
      var e0 = S.books[0].error;
      fail(errorText(e0), S.books[0].name);
      return;
    }
    var missing = ok.filter(function (b) { return b.info.missing && b.info.missing.length; });
    if (missing.length && ok.length === 1) {
      fail(t('err_missing_parts'), missing[0].info.missing.slice(0, 12).join(', ') + (missing[0].info.missing.length > 12 ? '…' : ''));
      return;
    }
    funnel('file_picked', {
      books: ok.length, bytes: ok.reduce(function (s, b) { return s + b.size; }, 0),
      pages: ok.reduce(function (s, b) { return s + b.sum.pages; }, 0),
      text: ok.reduce(function (s, b) { return s + b.sum.textPages; }, 0)
    });
    stage('editing');
    renderSetup();
    view('djvSetup');
    thumbs();
  }

  function errorText(err) {
    var code = err && err.code;
    if (code === 'NOT_DJVU' || code === 'UNSUPPORTED_FORM') return t('err_not_djvu');
    if (code === 'TRUNCATED') return t('err_truncated');
    if (code === 'MISSING_PART') return t('err_missing_parts');
    return t('err_read');
  }

  function summarise(b) {
    var pages = b.info.pages;
    var sum = { pages: pages.length, textPages: 0, colour: 0, photo: 0, broken: 0, dpi: 0, outline: countOutline(b.info.dir.outline) };
    var dpis = {};
    pages.forEach(function (p) {
      if (p.hasText) sum.textPages++;
      if (p.kind === 'compound') sum.colour++;
      else if (p.kind === 'photo') sum.photo++;
      else if (p.kind === 'broken') sum.broken++;
      if (p.dpi) dpis[p.dpi] = (dpis[p.dpi] || 0) + 1;
    });
    var best = 0;
    Object.keys(dpis).forEach(function (d) { if (dpis[d] > best) { best = dpis[d]; sum.dpi = +d; } });
    var meta = b.info.metadata || {};
    sum.title = meta.Title || meta.title || '';
    sum.author = meta.Author || meta.author || '';
    b.sum = sum;
  }

  // ------------------------------------------------------------------
  // Setup view.
  // ------------------------------------------------------------------
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function renderSetup() {
    var ok = S.books.filter(function (b) { return b.info; });
    var single = ok.length === 1;
    var box = $('djvBook');
    box.innerHTML = '';
    if (single) {
      var b = ok[0], s = b.sum;
      var head = el('div', 'djv-book-head');
      head.appendChild(el('div', 'djv-book-name', s.title || b.name.replace(/\.(djvu?|djv)$/i, '')));
      var sub = [];
      if (s.author) sub.push(s.author);
      sub.push(t('fact_pages', { n: s.pages }));
      if (s.dpi) sub.push(s.dpi + ' dpi');
      sub.push(fmtBytes(b.size));
      head.appendChild(el('div', 'djv-book-sub', sub.join(' · ')));
      box.appendChild(head);
      var chips = el('div', 'djv-chips');
      var txt = s.textPages === s.pages ? t('fact_text_all') : (s.textPages ? t('fact_text_some', { n: s.textPages, total: s.pages }) : t('fact_text_none'));
      chips.appendChild(chip(txt, s.textPages === s.pages ? 'ok' : (s.textPages ? 'part' : 'warn')));
      if (s.outline) chips.appendChild(chip(t('fact_outline', { n: s.outline }), 'ok'));
      if (s.colour + s.photo) chips.appendChild(chip(t('fact_colour', { n: s.colour + s.photo }), 'plain'));
      if (s.broken) chips.appendChild(chip(t('fact_broken', { n: s.broken }), 'warn'));
      if (b.indirect) chips.appendChild(chip(t('fact_indirect', { n: b.files.length }), 'plain'));
      box.appendChild(chips);
      var strip = el('div', 'djv-thumbs');
      strip.id = 'djvThumbs';
      var n = Math.min(s.pages, 6);
      for (var i = 0; i < n; i++) {
        var f = el('figure', 'djv-thumb');
        var c = document.createElement('canvas');
        c.width = 1; c.height = 1;
        c.setAttribute('role', 'img');
        c.setAttribute('aria-label', t('thumb_alt', { n: i + 1 }));
        f.appendChild(c);
        f.appendChild(el('figcaption', null, String(i + 1)));
        strip.appendChild(f);
      }
      box.appendChild(strip);
    } else {
      var list = el('ul', 'djv-list');
      ok.forEach(function (b) {
        var li = el('li', 'djv-list-i');
        li.appendChild(el('span', 'djv-list-name', b.name));
        var s = b.sum;
        var bits = [t('fact_pages', { n: s.pages }), fmtBytes(b.size)];
        bits.push(s.textPages === s.pages ? t('fact_text_all_short') : (s.textPages ? t('fact_text_some_short', { n: s.textPages }) : t('fact_text_none_short')));
        li.appendChild(el('span', 'djv-list-facts', bits.join(' · ')));
        list.appendChild(li);
      });
      S.books.filter(function (b) { return !b.info; }).forEach(function (b) {
        var li = el('li', 'djv-list-i is-bad');
        li.appendChild(el('span', 'djv-list-name', b.name));
        li.appendChild(el('span', 'djv-list-facts', errorText(b.error)));
        list.appendChild(li);
      });
      box.appendChild(el('div', 'djv-book-name', t('batch_title', { n: ok.length })));
      box.appendChild(list);
    }
    // pages range: only for one book
    show($('djvRangeRow'), single);
    if (single) {
      var total = ok[0].sum.pages;
      $('djvFrom').max = String(total); $('djvTo').max = String(total);
      $('djvFrom').value = '1'; $('djvTo').value = String(total);
      $('djvRangeAll').checked = true;
      syncRange();
    }
    // OCR offer: pages without a text layer exist
    var noText = ok.reduce(function (s, b) { return s + (b.sum.pages - b.sum.textPages); }, 0);
    var allPages = ok.reduce(function (s, b) { return s + b.sum.pages; }, 0);
    show($('djvOcrRow'), noText > 0);
    if (noText > 0) {
      $('djvOcrNote').textContent = t(noText === allPages ? 'ocr_note_all' : 'ocr_note_some', { n: noText });
      // on by default when the book has no text at all and is not huge
      $('djvOcr').checked = noText === allPages && noText <= 150;
      syncOcr();
    }
    estimate();
  }

  function chip(text, kind) {
    var c = el('span', 'djv-chip djv-chip-' + kind, text);
    return c;
  }

  async function thumbs() {
    var ok = S.books.filter(function (b) { return b.info; });
    if (ok.length !== 1) return;
    var b = ok[0];
    var strip = $('djvThumbs');
    if (!strip) return;
    var cans = strip.querySelectorAll('canvas');
    for (var i = 0; i < cans.length; i++) {
      if (S.running || !b.job) return;
      try {
        var pic = await b.job.render(i, 176);
        var c = cans[i];
        c.width = pic.width; c.height = pic.height;
        c.getContext('2d').putImageData(new ImageData(pic.data, pic.width, pic.height), 0, 0);
        if (pic.rotation) c.style.transform = 'rotate(' + pic.rotation + 'deg)';
        c.parentNode.classList.add('is-ready');
      } catch (e) {
        if (e && e.code === 'USER_CANCELLED') return;
        cans[i].parentNode.classList.add('is-broken');
      }
    }
  }

  function preset() {
    var r = document.querySelector('input[name="djvPreset"]:checked');
    return r ? r.value : 'original';
  }
  function range() {
    var ok = S.books.filter(function (b) { return b.info; });
    if (ok.length !== 1 || $('djvRangeAll').checked) return null;
    var total = ok[0].sum.pages;
    var a = Math.max(1, Math.min(total, parseInt($('djvFrom').value, 10) || 1));
    var z = Math.max(1, Math.min(total, parseInt($('djvTo').value, 10) || total));
    if (z < a) { var tmp = a; a = z; z = tmp; }
    return { from: a - 1, to: z - 1 };
  }
  function syncRange() {
    var all = $('djvRangeAll').checked;
    $('djvFrom').disabled = all; $('djvTo').disabled = all;
    estimate();
  }
  function syncOcr() {
    show($('djvOcrLangRow'), $('djvOcr').checked);
    estimate();
  }

  // What the run will do: pages, and the OCR time guess (a phone reads a
  // page in several seconds, a desktop in one or two).
  function estimate() {
    var ok = S.books.filter(function (b) { return b.info; });
    var pages = 0, noText = 0;
    var r = range();
    ok.forEach(function (b) {
      var from = r ? r.from : 0, to = r ? r.to : b.sum.pages - 1;
      for (var i = from; i <= to; i++) { pages++; if (!b.info.pages[i].hasText) noText++; }
    });
    var ocrOn = $('djvOcr').checked && noText > 0;
    var parts = [t('est_pages', { n: pages })];
    if (ocrOn) {
      var perPage = (navigator.hardwareConcurrency || 4) >= 8 ? 1.5 : 4;
      parts.push(t('est_ocr', { n: noText, t: fmtTime(noText * perPage) }));
    }
    $('djvEstimate').textContent = parts.join(' · ');
  }

  // ------------------------------------------------------------------
  // Conversion.
  // ------------------------------------------------------------------
  function setBar(id, frac) {
    var b = $(id);
    if (b) b.style.width = Math.max(0, Math.min(100, frac * 100)).toFixed(1) + '%';
  }

  async function openOcr() {
    if (S.ocr && S.ocr.lang === $('djvOcrLang').value) return S.ocr.engine;
    if (!window.OcrEngine) throw Object.assign(new Error('OCR engine missing'), { code: 'OCR_MISSING' });
    var lang = $('djvOcrLang').value;
    $('djvPhase').textContent = t('phase_ocr_model');
    var engine = await window.OcrEngine.open(lang, {
      slug: SLUG,
      tesseractUrl: CFG.tesseractUrl,
      onProgress: function (p) {
        if (typeof p === 'number') $('djvPhase').textContent = t('phase_ocr_model') + ' ' + Math.round(p * 100) + '%';
      }
    });
    if (S.ocr && S.ocr.engine && S.ocr.engine !== engine) { try { S.ocr.engine.close(); } catch (e) { /* */ } }
    S.ocr = { lang: lang, engine: engine };
    return engine;
  }

  async function convert() {
    if (S.running) return;
    var books = S.books.filter(function (b) { return b.info; });
    if (!books.length) return;
    var TL = window.__toolLimit;
    var keys = books.map(function (b) { return b.files[b.main]; });
    if (TL) {
      S.ticket = TL.begin(SLUG, TL.fileKeys ? TL.fileKeys(keys) : keys.map(function (f) { return f.name + ':' + f.size; }));
      if (!S.ticket) return;
    }
    S.running = true;
    S.abort = new AbortController();
    var signal = S.abort.signal;
    var pre = preset();
    var r = range();
    var masks = $('djvCompat').checked ? 'g4' : 'jbig2';
    var wantOcr = $('djvOcr').checked && !$('djvOcrRow').hidden;
    view('djvWork');
    setBar('djvBar', 0);
    $('djvEta').textContent = '';
    $('djvPhase').textContent = t('phase_start');
    funnel('convert_start', { preset: pre, ocr: wantOcr ? 1 : 0, masks: masks, books: books.length, range: r ? 1 : 0 });
    var t0 = Date.now();
    var engine = null;
    try {
      if (wantOcr) {
        try { engine = await openOcr(); }
        catch (e) {
          if (signal.aborted) throw e;
          toast(t('ocr_unavailable'), 'warning');
          ae('tool_error', { error_type: 'ocr_open', msg: String(e && e.message || e).slice(0, 120) });
          engine = null;
        }
      }
      var totalPages = books.reduce(function (s, b) { return s + (r ? r.to - r.from + 1 : b.sum.pages); }, 0);
      var donePages = 0;
      if (window.__acETA) window.__acETA.beginStage('djvu');
      for (var i = 0; i < books.length; i++) {
        var b = books[i];
        if (signal.aborted) throw cancelErr();
        var prefix = books.length > 1 ? (i + 1) + '/' + books.length + ' · ' : '';
        var meta = b.info.metadata || {};
        var before = donePages;
        var res = await b.job.convert({
          preset: pre,
          masks: masks,
          from: r ? r.from : 0,
          to: r ? r.to : b.sum.pages - 1,
          ocr: engine ? { engine: engine } : null,
          info: {
            Title: meta.Title || meta.title || '',
            Author: meta.Author || meta.author || '',
            Subject: meta.Subject || meta.subject || '',
            Keywords: meta.Keywords || meta.keywords || '',
            Creator: CFG.creator || 'DjVu to PDF'
          },
          lang: engine && S.ocr ? S.ocr.lang : null,
          signal: signal,
          onProgress: function (p) {
            if (p.phase === 'pages') {
              var done = before + p.done;
              donePages = done;
              var frac = totalPages ? done / totalPages : 0;
              setBar('djvBar', frac);
              $('djvPhase').textContent = prefix + t('phase_pages', { done: p.done, total: p.total });
              if (window.__acETA) {
                window.__acETA.updateRate('djvu', frac);
                var sec = window.__acETA.remainingSec('djvu');
                $('djvEta').textContent = sec ? t('eta_left', { t: fmtTime(sec) }) : '';
              }
            } else if (p.phase === 'ocr') {
              $('djvPhase').textContent = prefix + t('phase_ocr', { n: p.page, total: p.total });
            }
          }
        });
        b.blob = res.blob;
        b.stats = res.stats;
        b.outName = outputName(b, r);
      }
      if (window.__acETA) window.__acETA.endStage('djvu');
      S.running = false;
      var st = books.reduce(function (acc, b) {
        acc.pages += b.stats.pages; acc.bytes += b.stats.bytes; acc.ocr += b.stats.ocrPages; acc.text += b.stats.textPages; acc.failed += b.stats.failed.length;
        return acc;
      }, { pages: 0, bytes: 0, ocr: 0, text: 0, failed: 0 });
      try {
        if (typeof window.__aeToolComplete === 'function') {
          window.__aeToolComplete({
            books: books.length, pages: st.pages, preset: pre, masks: masks,
            bytes_in: books.reduce(function (s, b) { return s + b.size; }, 0), bytes_out: st.bytes,
            text_pages: st.text, ocr_pages: st.ocr, failed_pages: st.failed,
            ms: Date.now() - t0, encoder: books[0].stats.encoder || ''
          });
        }
      } catch (e) { /* */ }
      renderResult(books);
      view('djvResult');
    } catch (err) {
      S.running = false;
      if (window.__acETA) window.__acETA.endStage('djvu');
      if (S.ticket) { try { S.ticket.abort(); } catch (e) { /* */ } S.ticket = null; }
      var cancelled = typeof window.__isUserCancel === 'function' ? window.__isUserCancel(err, signal) : signal.aborted;
      books.forEach(function (b) { if (b.job) { try { b.job.close(); } catch (e) { /* */ } } });
      if (cancelled) {
        ae('feature_use', { f: 'cancel', ms: Date.now() - t0 });
        renderSetup();
        view('djvSetup');
        thumbs();
        return;
      }
      ae('tool_error', { error_type: 'convert_' + ((err && err.code) || 'error'), msg: String(err && err.message || err).slice(0, 160) });
      if (typeof window.__handleError === 'function') { try { console.error(err); } catch (e) { /* */ } }
      fail(t('err_failed'), String(err && err.message || err).slice(0, 200));
    }
  }

  function cancelErr() { var e = new Error('cancelled'); e.name = 'AbortError'; e.code = 'USER_CANCELLED'; return e; }

  function outputName(b, r) {
    var stem = b.name.replace(/\.(djvu?|djv)$/i, '');
    if (r) stem += ' (' + (r.from + 1) + '-' + (r.to + 1) + ')';
    var O = window.__outputName;
    if (O && O.join && O.sanitize) return O.join([O.sanitize(stem)], 'pdf');
    return stem.replace(/[\\/:*?"<>|]+/g, '_') + '.pdf';
  }

  // ------------------------------------------------------------------
  // Result.
  // ------------------------------------------------------------------
  function renderResult(books) {
    var box = $('djvDone');
    box.innerHTML = '';
    books.forEach(function (b, i) {
      var st = b.stats;
      var card = el('div', 'djv-done-card');
      var head = el('div', 'djv-done-head');
      head.appendChild(el('span', 'djv-done-name', b.outName));
      head.appendChild(el('span', 'djv-done-size', t('done_size', { size: fmtBytes(st.bytes), src: fmtBytes(b.size) })));
      card.appendChild(head);
      var checks = el('ul', 'djv-checks');
      checks.appendChild(check(t('check_pages', { n: st.pages })));
      var searchable = st.textPages + st.ocrPages;
      if (searchable) checks.appendChild(check(t('check_text', { n: searchable, total: st.pages })));
      else checks.appendChild(check(t('check_no_text'), 'warn'));
      if (st.ocrPages) checks.appendChild(check(t('check_ocr', { n: st.ocrPages })));
      var outl = countOutline(b.info.dir.outline);
      if (outl) checks.appendChild(check(t('check_outline', { n: outl })));
      if (st.links) checks.appendChild(check(t('check_links', { n: st.links })));
      if (st.failed.length) checks.appendChild(check(t('check_failed', { list: st.failed.slice(0, 10).join(', ') + (st.failed.length > 10 ? '…' : '') }), 'warn'));
      if (st.ocrFailed) checks.appendChild(check(t('check_ocr_failed', { n: st.ocrFailed }), 'warn'));
      card.appendChild(checks);
      var btn = el('button', 'lg-btn lg-btn-primary djv-dl', books.length > 1 ? t('download_one') : t('download'));
      btn.type = 'button';
      btn.setAttribute('data-tmb-api', 'download');
      btn.addEventListener('click', function () { deliver([b]); });
      card.appendChild(btn);
      box.appendChild(card);
    });
    show($('djvDownloadAll'), books.length > 1);
  }

  function check(text, kind) {
    var li = el('li', 'djv-check' + (kind ? ' is-' + kind : ''));
    li.textContent = text;
    return li;
  }

  // Hand the PDF(s) over — the moment a unit is billed.
  async function deliver(books) {
    books = books.filter(function (b) { return b.blob; });
    if (!books.length) return;
    try {
      if (books.length === 1) {
        window.__downloadFile(books[0].blob, books[0].outName);
      } else {
        var bd = window.__batchDeliver || (window.__ensureBatchDeliver ? await window.__ensureBatchDeliver() : null);
        if (!bd) { books.forEach(function (b) { window.__downloadFile(b.blob, b.outName); }); }
        else {
          var res = await bd.deliver(books.map(function (b) { return { name: b.outName, blob: b.blob }; }), { zipName: 'djvu-to-pdf.zip', slug: SLUG });
          if (res && (res.via === 'declined' || res.via === 'refused' || res.via === 'empty')) return;
        }
      }
    } catch (e) {
      if (typeof window.__isUserCancel === 'function' && window.__isUserCancel(e)) return;
      toast(t('err_save'));
      return;
    }
    books.forEach(function (b) { S.delivered.add(b); });
    bill();
    stage('complete');
    funnel('download', { books: books.length });
  }

  function bill() {
    var TL = window.__toolLimit;
    if (!S.ticket) return;
    var all = S.books.filter(function (b) { return b.blob; });
    if (all.every(function (b) { return S.delivered.has(b); })) {
      try { S.ticket.commit(); } catch (e) { /* */ }
      S.ticket = null;
      return;
    }
    // part of a batch handed over: bill those files, keep the rest open
    if (TL && TL.record && TL.fileKeys) {
      var fresh = [];
      S.delivered.forEach(function (b) { if (!b.billed) { b.billed = true; fresh.push(b.files[b.main]); } });
      if (fresh.length) TL.record(SLUG, TL.fileKeys(fresh));
    }
  }

  // ------------------------------------------------------------------
  // Arrival with the book already in hand.
  // /file-converter cannot read DjVu (6 sessions in 20 days hit that, and two
  // filed «DjVu → PDF» the same day, 2026-10-06); its row links here and puts
  // the file under HANDOFF_KEY first (window.__toolHandoffPut). The book goes
  // through the zone exactly as if it were dropped, so the survey, the cap
  // and the picked-file row all behave as usual. Single use: the take deletes
  // the carry, a stale one (30 min) comes back null.
  // ------------------------------------------------------------------
  var HANDOFF_KEY = 'fc:djvu';
  function takeHandoff(up) {
    if (typeof window.__toolHandoffTake !== 'function') return;
    window.__toolHandoffTake(HANDOFF_KEY).then(function (f) {
      if (!f || S.running || (S.books && S.books.length)) return;
      var tries = 0;
      // the zone wires itself on its own DOMContentLoaded — give it a moment
      (function put() {
        if (S.running || (S.books && S.books.length)) return;
        if (typeof up.__uploaderInjectFile === 'function') {
          up.__uploaderInjectFile(f, 'handoff');
          ae('feature_use', { f: 'handoff_in', from: 'file-converter', mb: Math.round(f.size / 1048576) });
          return;
        }
        if (++tries < 40) setTimeout(put, 100);
      })();
    }, function () { /* no carry — the page simply waits for a file */ });
  }

  // ------------------------------------------------------------------
  // Wiring.
  // ------------------------------------------------------------------
  function init() {
    var up = $('djvUpload');
    if (!up) return;
    var onPick = function (f, detail) { onFiles((detail && detail.files && detail.files.length) ? detail.files : [f]); };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, onPick);
    else up.addEventListener('tool-uploader:file', function (e) { var d = e.detail || {}; var f = d.file || (d.files && d.files[0]); if (f) onPick(f, d); });
    up.addEventListener('tool-uploader:clear', function () { if (!S.running) reset(); });
    $('djvConvert').addEventListener('click', convert);
    $('djvCancel').addEventListener('click', function () { if (S.abort) S.abort.abort(); });
    $('djvAnother').addEventListener('click', reset);
    $('djvAnother2').addEventListener('click', reset);
    $('djvAnother3').addEventListener('click', reset);
    $('djvDownloadAll').addEventListener('click', function () { deliver(S.books.filter(function (b) { return b.blob; })); });
    $('djvRangeAll').addEventListener('change', syncRange);
    $('djvRangeSome').addEventListener('change', syncRange);
    ['djvFrom', 'djvTo'].forEach(function (id) {
      $(id).addEventListener('input', estimate);
      $(id).addEventListener('focus', function () { if ($('djvRangeAll').checked) { $('djvRangeSome').checked = true; syncRange(); } });
    });
    $('djvOcr').addEventListener('change', syncOcr);
    document.querySelectorAll('input[name="djvPreset"]').forEach(function (r) { r.addEventListener('change', estimate); });
    // settings persistence (the shared helper, slug first)
    var TS = window.__toolSettings;
    if (TS && TS.bind) {
      try {
        TS.bind(SLUG, $('djvCompat'), 'compat', false);
        TS.bind(SLUG, $('djvOcrLang'), 'ocr_lang', $('djvOcrLang').value);
      } catch (e) { /* defaults stay */ }
      try {
        var saved = TS.load(SLUG, 'preset', 'original');
        var r = document.querySelector('input[name="djvPreset"][value="' + saved + '"]');
        if (r) r.checked = true;
        document.querySelectorAll('input[name="djvPreset"]').forEach(function (x) {
          x.addEventListener('change', function () { if (x.checked) TS.save(SLUG, 'preset', x.value); });
        });
      } catch (e2) { /* */ }
    }
    takeHandoff(up);
    window.addEventListener('pagehide', function () { if (!S.running) closeAll(); });
    // the decoder's scripts start loading before the file is picked
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
    idle(function () { if (window.DjVuJob && window.DjVuJob.prewarm) window.DjVuJob.prewarm(CFG.workerUrl); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
